import bgImg from '@/assets/img/curved-images/top_bg.svg';
import { ApiErrorMessages } from '@/components/element/ApiErrorMessages';
import { SpriteSvg } from '@/components/element/SpriteSvg';
import { SessionLayout } from '@/components/layout/SessionLayout';
import { useCreateEmailRegistration } from '@/hooks/api/0010_email_registration';
import { useCreateThirdPartyAuthentication } from '@/hooks/api/0030_third_party_authentication';
import { useGoogleAuth } from '@/hooks/useGoogleAuth';
import { useLoading } from '@/providers/LoadingProvider';
import { useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

type FormInputs = {
  email: string;
  password: string;
  password_confirmation: string;
  agree_to_term: boolean;
  invitation: string | null;
};

export const SignUp = () => {
  const [searchParams] = useSearchParams();
  const invitation = searchParams.get('invitation');
  const navigate = useNavigate();
  const { setShowLoading } = useLoading();
  const { idToken, signUpWithGoogle } = useGoogleAuth();
  const { mutate, data, isPending, error } = useCreateEmailRegistration();
  const {
    mutate: mutateForGoogle,
    data: dataForGoogle,
    isPending: isPendingForGoogle,
    isSuccess: isSuccessForGoogle,
    error: errorForGoogle,
  } = useCreateThirdPartyAuthentication();

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<FormInputs>({ mode: 'onBlur', criteriaMode: 'all', defaultValues: { invitation } });

  const onSubmit: SubmitHandler<FormInputs> = (data) => {
    setShowLoading(true);
    mutate(data);
  };

  useEffect(() => {
    setShowLoading(isPending || isPendingForGoogle);
  }, [isPending, isPendingForGoogle]);

  useEffect(() => {
    if (data) {
      navigate('/sign_up/verify_code', {
        state: { token: data!.email_registration_token, invitation },
      });
    }
  }, [data]);

  useEffect(() => {
    // Google認証でid_tokenを取得出来た場合はSignUpを実行
    if (idToken) {
      mutateForGoogle({ id_token: idToken, invitation });
    }
  }, [idToken]);

  useEffect(() => {
    if (dataForGoogle) {
      navigate('/workspaces', { state: { fromLocation: 'sign_up' } });
    }
  }, [dataForGoogle]);

  return (
    <SessionLayout>
      <main className="main-content mt-0">
        <div
          className="page-header align-items-start min-vh-50 pt-5 pb-11 m-3 border-radius-lg"
          style={{ backgroundImage: `url(${bgImg})`, backgroundColor: '#FBF6FF' }}
        >
          <span className="mask bg-gradient-dark opacity-5"></span>
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-lg-5 text-center mx-auto">
                <h1 className="text-white mb-2 mt-5">Nodeへようこそ</h1>
                <p className="text-lead text-white">
                  Nodeはプロジェクトの管理・バックオフフィス担当との
                  <br />
                  連携をスムーズにするサービスです。
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="container">
          <div className="row mt-lg-n10 mt-md-n11 mt-n10 justify-content-center">
            <div className="col-xl-4 col-lg-5 col-md-7 mx-auto">
              <div className="card z-index-0">
                <div className="card-header text-center pt-4">
                  <h5>無料アカウントを作成</h5>
                  <span className="text-muted text-sm">
                    料金はかかりません。<br/>クレジットカードなどの決済情報も不要です。
                  </span>
                </div>
                <div className="card-body">
                  <ApiErrorMessages error={errorForGoogle} />

                  <button className="btn btn-outline-secondary w-100 mb-2" onClick={signUpWithGoogle}>
                    <SpriteSvg name={'icon-google'} />
                    Googleで続ける
                  </button>
                  <div className="mb-2 position-relative text-center">
                    <p className="text-sm font-weight-bold mb-2 text-secondary text-border d-inline z-index-2 bg-white px-3">
                      or
                    </p>
                  </div>
                  <form role="form" className="text-start" onSubmit={handleSubmit(onSubmit)}>
                    <ApiErrorMessages error={error} />

                    <div className="mb-3">
                      <input
                        type="email"
                        maxLength={100}
                        className={`form-control ${errors.email && 'is-invalid'}`}
                        placeholder={'メールアドレス'}
                        {...register('email', {
                          required: '入力して下さい。',
                          pattern: {
                            value: /^[\w\-._+]+@[\w\-._]+\.[A-Za-z]+/,
                            message: 'メールアドレスが不正です。',
                          },
                        })}
                      />
                      {errors.email && <div className="text-xs text-danger mt-1">{errors.email.message}</div>}
                    </div>
                    <div className="mb-3">
                      <input
                        type="password"
                        maxLength={50}
                        className={`form-control ${errors.password && 'is-invalid'}`}
                        placeholder={'パスワード'}
                        {...register('password', {
                          required: '入力して下さい。',
                          pattern: {
                            value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_])[!-~]{8,50}$/,
                            message: '半角英数・大文字小文字・記号を組み合わせた8文字以上のパスワードにしてください。',
                          },
                        })}
                      />
                      {errors.password && <div className="text-xs text-danger mt-1">{errors.password.message}</div>}
                    </div>
                    <div className="mb-3">
                      <input
                        type="password"
                        maxLength={50}
                        className={`form-control ${errors.password_confirmation && 'is-invalid'}`}
                        placeholder={'パスワード（再入力）'}
                        {...register('password_confirmation', {
                          required: '入力して下さい。',
                          validate: (value) => value == getValues('password') || 'パスワードが一致しません。',
                        })}
                      />
                      {errors.password_confirmation && (
                        <div className="text-xs text-danger mt-1">{errors.password_confirmation.message}</div>
                      )}
                    </div>
                    <div className="form-check form-switch mb-3">
                      <input
                        type="checkbox"
                        id="confirm"
                        className="form-check-input"
                        {...register('agree_to_term', {
                          required: '登録には利用規約とプライバシーポリシーへの同意が必要です。',
                        })}
                      />
                      <label className="form-check-label" htmlFor="confirm">
                        <a href="https://biz-node.app/legal/terms" className="text-decoration-underline" target="_blank" rel="noopener noreferrer">
                          利用規約
                        </a>
                        と
                        <a href="https://biz-node.app/legal/privacy" className="text-decoration-underline" target="_blank" rel="noopener noreferrer">
                          プライバシーポリシー
                        </a>
                        に同意する
                      </label>
                      {errors.agree_to_term && (
                        <div className="text-xs text-danger mt-1">{errors.agree_to_term.message}</div>
                      )}
                    </div>
                    <div className="text-center mb-3">
                      <button type="submit" className="btn bg-gradient-info w-100 mb-2">
                        利用開始
                      </button>
                      <div className="mb-4 text-center text-dark text-xxs">
                        お客さまに関連するコンテンツ、製品、サービスについて<br/>
                        ご連絡を差し上げる場合があります。配信はいつでも停止できます。<br/>
                        詳しくは、<a href="https://biz-node.app/legal/privacy" className="text-decoration-underline" target="_blank" rel="noopener noreferrer">
                          プライバシーポリシー
                        </a>
                        をご確認ください。
                      </div>
                      <span className="text-muted text-sm">
                        すでにアカウントをお持ちの方は
                        <Link to={`/sign_in${invitation ? `?invitation=${invitation}` : ''}`} className="text-decoration-underline">こちら</Link>
                      </span>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </SessionLayout>
  );
};
